/**
 * GeoJSON Loader Module
 * Handles loading and parsing GeoJSON files
 */

/**
 * Validate GeoJSON structure
 * @param {Object} geojson - GeoJSON object
 * @returns {boolean} True if valid
 */
function validateGeoJSON(geojson) {
    if (!geojson || typeof geojson !== 'object') {
        throw new Error('Invalid GeoJSON: not an object');
    }

    if (!geojson.type) {
        throw new Error('Invalid GeoJSON: missing type property');
    }

    // Accept FeatureCollection, Feature, or Geometry types
    const validTypes = [
        'FeatureCollection',
        'Feature',
        'Point', 'MultiPoint',
        'LineString', 'MultiLineString',
        'Polygon', 'MultiPolygon',
        'GeometryCollection'
    ];

    if (!validTypes.includes(geojson.type)) {
        throw new Error(`Invalid GeoJSON type: ${geojson.type}`);
    }

    return true;
}

/**
 * Normalize GeoJSON to FeatureCollection
 * @param {Object} geojson - GeoJSON object
 * @returns {Object} FeatureCollection
 */
function normalizeToFeatureCollection(geojson) {
    // Already a FeatureCollection
    if (geojson.type === 'FeatureCollection') {
        return geojson;
    }

    // Single Feature
    if (geojson.type === 'Feature') {
        return {
            type: 'FeatureCollection',
            features: [geojson]
        };
    }

    // Geometry object (Point, LineString, Polygon, etc.)
    if (geojson.type !== 'FeatureCollection' && geojson.type !== 'Feature') {
        return {
            type: 'FeatureCollection',
            features: [{
                type: 'Feature',
                geometry: geojson,
                properties: {}
            }]
        };
    }

    return geojson;
}

/**
 * Add feature indices to properties
 * @param {Object} featureCollection - GeoJSON FeatureCollection
 * @returns {Object} FeatureCollection with indices
 */
function addFeatureIndices(featureCollection) {
    if (featureCollection.features) {
        featureCollection.features.forEach((feature, index) => {
            if (!feature.properties) {
                feature.properties = {};
            }
            feature.properties.__featureIndex = index;
        });
    }
    return featureCollection;
}

/**
 * Load and parse GeoJSON file
 * @param {File} file - GeoJSON file
 * @param {Function} progressCallback - Progress callback (progress, message)
 * @returns {Object} Result with geojson, fileName, and featureCount
 */
export async function loadGeoJSON(file, progressCallback) {
    try {
        progressCallback(10, 'Reading GeoJSON file...');

        // Read file as text
        const text = await file.text();

        progressCallback(30, 'Parsing GeoJSON...');

        // Parse JSON
        let geojson;
        try {
            geojson = JSON.parse(text);
        } catch (error) {
            throw new Error(`Failed to parse JSON: ${error.message}`);
        }

        progressCallback(50, 'Validating GeoJSON...');

        // Validate GeoJSON structure
        validateGeoJSON(geojson);

        progressCallback(70, 'Processing features...');

        // Normalize to FeatureCollection
        geojson = normalizeToFeatureCollection(geojson);

        // Add feature indices
        geojson = addFeatureIndices(geojson);

        // Check if we have any features
        if (!geojson.features || geojson.features.length === 0) {
            throw new Error('GeoJSON contains no features');
        }

        progressCallback(90, 'Processing complete...');

        // Get geometry types
        const geometryTypes = new Set();
        geojson.features.forEach(feature => {
            if (feature.geometry && feature.geometry.type) {
                geometryTypes.add(feature.geometry.type);
            }
        });

        return {
            geojson,
            fileName: file.name.replace(/\.(geo)?json$/i, ''),
            featureCount: geojson.features.length,
            geometryTypes: Array.from(geometryTypes)
        };

    } catch (error) {
        console.error('Error loading GeoJSON:', error);
        throw error;
    }
}

/**
 * Setup drag and drop for GeoJSON files
 * @param {HTMLElement} dropArea - Drop area element
 * @param {Function} handleFile - File handler callback
 */
export function setupGeoJSONDragAndDrop(dropArea, handleFile) {
    // Prevent default drag behaviors
    ['dragenter', 'dragover', 'dragleave', 'drop'].forEach(eventName => {
        dropArea.addEventListener(eventName, preventDefaults, false);
    });

    function preventDefaults(e) {
        e.preventDefault();
        e.stopPropagation();
    }

    // Highlight drop area
    ['dragenter', 'dragover'].forEach(eventName => {
        dropArea.addEventListener(eventName, () => {
            dropArea.classList.add('drag-over');
        }, false);
    });

    ['dragleave', 'drop'].forEach(eventName => {
        dropArea.addEventListener(eventName, () => {
            dropArea.classList.remove('drag-over');
        }, false);
    });

    // Handle dropped files
    dropArea.addEventListener('drop', (e) => {
        const dt = e.dataTransfer;
        const files = dt.files;

        if (files.length > 0) {
            handleFile(files[0]);
        }
    }, false);
}
